import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { ObjHlp } from 'src/lib/fliko/helpers/object.helper';
import { ResolutionStatus } from 'src/lib/fliko/models/resolutions/resolution-enum';
import {
  ResolutionData,
  ResolutionDetailsData,
  ResolutionHistoryRecord,
  ResolutionVoteData
} from 'src/lib/fliko/models/resolutions/resolution.data';
import { Collection } from 'src/lib/fliko/types/database.type';
import { Nullish } from 'src/lib/fliko/types/typeish.type';
import { UpdateIdOnly } from 'src/lib/fliko/types/update.type';
import { SortHelper } from 'src/app/shared/helpers/sort.helper';
import { SortCriteria } from 'src/app/shared/models/sort-criteria';
import { DatabaseService } from 'src/app/shared/services/database.service';

@Injectable({
  providedIn: 'root'
})
export class ResolutionService {
  constructor(private dbSrv: DatabaseService) {}

  // #region Resolution
  /**
   * @path /resolution/$estateId/
   * @key id
   */
  private get resolution() {
    return this.dbSrv.estateFactory.getCollection<ResolutionData>('resolution/$eId');
  }

  private get resolutionActive() {
    return this.dbSrv.estateFactory.getCollectionReadOnly<ResolutionData>('resolution/$eId', {
      by: { child: 'status' },
      equalTo: ResolutionStatus.ACTIVE
    });
  }

  public getResolutions$(): Observable<ResolutionData[]> {
    return this.resolution.getList();
  }

  public getResolution$(id: string): Observable<ResolutionData | Nullish> {
    return this.resolution.getElement(id);
  }

  public getActiveResolutions$(sortCriteria?: SortCriteria): Observable<ResolutionData[]> {
    return this.resolutionActive.getList().pipe(map((res) => res.sort(SortHelper.getComp(sortCriteria))));
  }

  public setResolution(resolution: ResolutionData): Promise<void> {
    return this.resolution.setElement(resolution.id, resolution);
  }

  public updateResolution(resolution: UpdateIdOnly<ResolutionData>): Promise<void> {
    return this.resolution.updateElement(resolution.id, resolution);
  }

  public removeResolution(id: string): Promise<void> {
    return this.resolution.removeElement(id);
  }
  // #endregion Resolution

  // #region Details
  /**
   * @path /resolutionDetail/$estateId/
   * @key resolutionId
   */
  private get resolutionDetail() {
    return this.dbSrv.estateFactory.getCollection<ResolutionDetailsData>('resolutionDetail/$eId');
  }

  public getDetails$(): Observable<ResolutionDetailsData[]> {
    return this.resolutionDetail.getList();
  }

  public setDetails(details: ResolutionDetailsData): Promise<void> {
    return this.resolutionDetail.setElement(details.id, details);
  }

  public updateDetails(details: UpdateIdOnly<ResolutionDetailsData>): Promise<void> {
    return this.resolutionDetail.updateElement(details.id, details);
  }

  // #endregion Details

  // #region Votes
  /**
   * @param resolutionId
   * @path /resolutionVote/$eId/$resolutionId/
   * @key id
   */
  private resolutionVote = (resolutionId: string) =>
    this.dbSrv.estateFactory.getCollection<ResolutionVoteData>(`resolutionVote/$eId/${resolutionId}`);

  /**
   * @path /resolutionVote/$estateId/
   * @key resolutionId
   */
  private get allResolutionVotes() {
    return this.dbSrv.estateFactory.getCollectionReadOnly<Collection<ResolutionVoteData>>('resolutionVote/$eId');
  }

  public getAllVotes$(): Observable<ResolutionVoteData[]> {
    return this.allResolutionVotes.getObject().pipe(map((obj) => ObjHlp.flatten(obj)));
  }

  public getVotes$(resolutionId: string): Observable<ResolutionVoteData[]> {
    return this.resolutionVote(resolutionId).getList();
  }

  public setResolutionVote(vote: ResolutionVoteData): Promise<void> {
    return this.resolutionVote(vote.resolutionId).setElement(vote.id, vote);
  }

  public updateResolutionVote(vote: ResolutionVoteData): Promise<void> {
    return this.resolutionVote(vote.resolutionId).updateElement(vote.id, vote);
  }

  public updateVotes(resolutionId: string, votes: ResolutionVoteData[]): Promise<void> {
    return this.resolutionVote(resolutionId).updateList(votes, 'id');
  }

  public removeVote(resolutionId: string, voteId: string): Promise<void> {
    return this.resolutionVote(resolutionId).removeElement(voteId);
  }
  // #endregion Votes

  /**
   * @path /resolutionHistoryRecord/$estateId/
   * @key id
   */
  private get resolutionHistory() {
    return this.dbSrv.estateFactory.getCollectionReadOnly<ResolutionHistoryRecord>('resolutionHistoryRecord/$eId');
  }

  public getResolutionsHistoryRecords$(): Observable<ResolutionHistoryRecord[]> {
    return this.resolutionHistory.getList();
  }
}
